import React, { useEffect, useState } from 'react'
import { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { connect, useDispatch } from 'react-redux'
import {
  Button,
  MaterialIcon,
  TextField
} from '@dsny/dsny-component-library'
import PlaylistActions, { thunks as PlayListThunks } from 'reducers/PlayList'
import ModalActions from 'reducers/Modal'
import NotificationActions from 'reducers/Notification'
import { NotificationType } from 'constants/enums'
import { trackEvent } from 'utils/segmentService'
import usePlayerPlaylists from 'utils/hooks/usePlayerPlaylists'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'
import {
  ButtonWrapper,
  CharacterCounter,
  CloseButton,
  DescriptionCounter,
  FieldGroup,
  FieldLabel,
  FieldLabelRow,
  FormError,
  ModalHeader,
  ModalTitle,
  PlaylistForm,
  SubmitButtonWrapper,
  Title,
  XButtonWrapper
} from './CreatePlaylistModal.styles'
import { StyledModal } from '../Modals.styles'

const MAX_TITLE_LIMIT = 50
const MAX_DESCRIPTION_LIMIT = 100

const mapStateToProps = ({ playList, modal }) => ({
  playlist: playList?.playlistUnderEditing,
  songsToAdd: playList?.songsToAdd,
  showPlaylistModal: modal?.showPlaylistModal,
  source: modal?.playlistModalSource || 'unknown'
})

export function CreatePlaylistModal({
  playlist,
  songsToAdd = [],
  showPlaylistModal,
  source
}) {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useDispatch()
  const isMobile = useMobileMediaQuery()
  const { enabled, isLoading: isRolloutLoading } = usePlayerPlaylists()
  const isRedesigned = enabled && !isRolloutLoading
  const isEditing = Boolean(playlist)
  const [title, setTitle] = useState(playlist?.name || '')
  const [message, setMessage] = useState(playlist?.desc || '')
  const [hasTitleError, setHasTitleError] = useState(false)
  const [submitErrorKey, setSubmitErrorKey] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [createdPlaylistId, setCreatedPlaylistId] = useState(null)
  const normalizedTitle = title.trim()
  const isSubmitDisabled = isSubmitting || !normalizedTitle

  useEffect(() => {
    setTitle(playlist?.name || '')
    setMessage(playlist?.desc || '')
    setHasTitleError(false)
    setSubmitErrorKey('')
    setCreatedPlaylistId(null)
  }, [playlist])

  useEffect(
    () => () => {
      dispatch(PlaylistActions.setPlaylistUnderEditing(null))
      dispatch(PlaylistActions.setSongsToAdd([]))
    },
    [dispatch]
  )

  const closeModal = () => {
    dispatch(ModalActions.closePlaylistModal())
  }

  const handleCancel = () => {
    if (!isSubmitting) {
      closeModal()
    }
  }

  const handleTitleChange = (event) => {
    const nextTitle = event.currentTarget.value

    setTitle(nextTitle)
    setHasTitleError(!nextTitle.trim())
    setSubmitErrorKey('')
  }

  const handleDescriptionChange = (event) => {
    setMessage(event.currentTarget.value)
    setSubmitErrorKey('')
  }

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!normalizedTitle) {
      setHasTitleError(true)
      return
    }

    setHasTitleError(false)
    setSubmitErrorKey('')
    setIsSubmitting(true)
    let playlistWasCreated = Boolean(createdPlaylistId)

    try {
      if (isEditing) {
        await dispatch(
          PlayListThunks.updatePlaylist({
            ...playlist,
            name: normalizedTitle,
            desc: message
          })
        )

        dispatch(
          NotificationActions.broadcastMessage(
            normalizedTitle,
            NotificationType.playlistUpdated
          )
        )
      } else {
        let playlistId = createdPlaylistId

        if (!playlistId) {
          const createdPlaylist = await dispatch(
            PlayListThunks.create({
              name: normalizedTitle,
              desc: message,
              default: false
            })
          )
          playlistId = createdPlaylist?.playlistid

          if (!playlistId) {
            throw new Error('Playlist creation returned no playlist ID')
          }

          setCreatedPlaylistId(playlistId)
          playlistWasCreated = true
          if (isRedesigned) {
            trackEvent('Playlist Created', {
              playlist_id: String(playlistId),
              source
            })
          }
        }

        if (Array.isArray(songsToAdd) && songsToAdd.length > 0) {
          await dispatch(
            PlayListThunks.addTracks(playlistId, songsToAdd, null, true)
          )
          dispatch(PlaylistActions.setSongsToAdd([]))
        }

        dispatch(
          NotificationActions.broadcastMessage(
            normalizedTitle,
            NotificationType.playlistCreated
          )
        )
      }

      setIsSubmitting(false)
      closeModal()
    } catch (error) {
      setSubmitErrorKey(
        playlistWasCreated
          ? 'playlist.selected-tracks-add-error'
          : 'playlist.save-error'
      )
      setIsSubmitting(false)
    }
  }

  const renderLegacyContent = () => (
    <>
      <XButtonWrapper
        aria-label={t('general.cancel')}
        disabled={isSubmitting}
        type="button"
        onClick={handleCancel}
      >
        <MaterialIcon name="X" color={theme.colors.neutralW80} />
      </XButtonWrapper>

      <Title>
        {isEditing
          ? t('playlist.editing-playlist', { name: playlist.name })
          : t('playlist.create-a-new-playlist')}
      </Title>

      <TextField
        autoFocus
        error={hasTitleError ? t('playlist.playlist-name-input-error') : ''}
        errorStyle={{ marginTop: 8, color: theme.colors.error70 }}
        id="playlist-name"
        inputStyle={{ color: theme.colors.neutralW100 }}
        labelText={t('playlist.playlist-name')}
        maxLength={MAX_TITLE_LIMIT}
        name="playlistName"
        required
        setting="player"
        value={title}
        wrapperStyles={{ marginBottom: 24, paddingRight: 0 }}
        onChange={handleTitleChange}
        onEnter={handleSubmit}
      />

      <TextField
        id="playlist-description"
        inputStyle={{ color: theme.colors.neutralW100 }}
        labelText={t('playlist.description-optional-label')}
        maxLength={MAX_DESCRIPTION_LIMIT}
        multiline
        name="playlistDescription"
        setting="player"
        value={message}
        wrapperStyles={{ paddingRight: 0, marginBottom: 4 }}
        onChange={handleDescriptionChange}
      />

      <DescriptionCounter>
        {`${message.length}/${MAX_DESCRIPTION_LIMIT}`}
      </DescriptionCounter>

      {submitErrorKey && (
        <FormError aria-live="polite" role="alert">
          {t(submitErrorKey)}
        </FormError>
      )}

      <ButtonWrapper>
        <Button
          disabled={isSubmitDisabled}
          setting="player"
          type="submit"
          variant="primary"
        >
          {isEditing
            ? t('playlist.update-playlist')
            : t('playlist.create-playlist')}
        </Button>
      </ButtonWrapper>
    </>
  )

  const renderRedesignedContent = () => (
    <>
      <ModalHeader>
        <ModalTitle $isMobile={isMobile}>
          {isEditing
            ? t('playlist.editing-playlist', { name: playlist.name })
            : t('playlist.create-a-new-playlist')}
        </ModalTitle>
        <CloseButton
          aria-label={t('general.cancel')}
          disabled={isSubmitting}
          type="button"
          onClick={handleCancel}
        >
          <MaterialIcon name="X" />
        </CloseButton>
      </ModalHeader>

      <FieldGroup>
        <FieldLabelRow>
          <FieldLabel htmlFor="playlist-name">
            <span aria-hidden="true">*</span>
            {t('playlist.playlist-name')}
          </FieldLabel>
          <CharacterCounter>
            {`${title.length}/${MAX_TITLE_LIMIT}`}
          </CharacterCounter>
        </FieldLabelRow>
        <TextField
          autoFocus
          aria-required="true"
          error={hasTitleError}
          hideInlineMessage
          hideLabel
          id="playlist-name"
          inputStyle={{
            color: theme.colors.neutralW100,
            fontSize: 14,
            height: 44,
            padding: '12px 14px'
          }}
          maxLength={MAX_TITLE_LIMIT}
          name="playlistName"
          placeholder={t('playlist.name-placeholder')}
          setting="player"
          value={title}
          wrapperStyles={{ margin: 0, padding: 0 }}
          onChange={handleTitleChange}
          onEnter={handleSubmit}
        />
        {hasTitleError && (
          <FormError id="error-playlist-name" role="alert">
            {t('playlist.playlist-name-input-error')}
          </FormError>
        )}
      </FieldGroup>

      <FieldGroup>
        <FieldLabelRow>
          <FieldLabel htmlFor="playlist-description">
            {t('playlist.description-optional-label')}
          </FieldLabel>
          <CharacterCounter>
            {`${message.length}/${MAX_DESCRIPTION_LIMIT}`}
          </CharacterCounter>
        </FieldLabelRow>
        <TextField
          hideLabel
          id="playlist-description"
          inputStyle={{
            color: theme.colors.neutralW100,
            fontSize: 14,
            height: 99,
            padding: '12px 14px',
            resize: 'none'
          }}
          maxLength={MAX_DESCRIPTION_LIMIT}
          multiline
          name="playlistDescription"
          placeholder={t('playlist.description-placeholder')}
          rows={3}
          setting="player"
          value={message}
          wrapperStyles={{ margin: 0, padding: 0 }}
          onChange={handleDescriptionChange}
        />
      </FieldGroup>

      {submitErrorKey && (
        <FormError aria-live="polite" role="alert">
          {t(submitErrorKey)}
        </FormError>
      )}

      <SubmitButtonWrapper $isMobile={isMobile}>
        <Button
          disabled={isSubmitDisabled}
          setting="player"
          type="submit"
          variant="primary"
        >
          {isEditing
            ? t('playlist.save-changes')
            : t('playlist.create-playlist')}
        </Button>
      </SubmitButtonWrapper>
    </>
  )

  return (
    <StyledModal
      $isMobile={isMobile}
      analyticsModalType={
        isRedesigned
          ? `playlist_${isEditing ? 'edit' : 'create'}`
          : undefined
      }
      containerStyles={
        isRedesigned
          ? {
              boxSizing: 'border-box',
              maxWidth: isMobile ? 'calc(100% - 32px)' : '624px',
              overflow: 'hidden',
              padding: isMobile ? '24px 12px' : '32px',
              width: isMobile ? '288px' : '624px'
            }
          : {
              padding: isMobile ? '48px 16px' : '24px 64px 64px'
            }
      }
      isEscKeypressDisabled={isSubmitting}
      isMaskClickDisabled={isSubmitting}
      isOpen={showPlaylistModal}
      trackEvent={isRedesigned ? trackEvent : undefined}
      onClose={handleCancel}
    >
      <PlaylistForm
        $isMobile={isRedesigned && isMobile}
        $redesigned={isRedesigned}
        onSubmit={handleSubmit}
      >
        {isRedesigned ? renderRedesignedContent() : renderLegacyContent()}
      </PlaylistForm>
    </StyledModal>
  )
}

export default connect(mapStateToProps)(CreatePlaylistModal)
